import { z } from 'zod';
import { PrincipalSchema, ToolNameSchema } from './access.js';
import { GrainSchema } from './catalog.js';
import { IdSchema, PublicationIdSchema, ResultIdSchema, TenantIdSchema } from './common.js';
import { FeedbackKindSchema, QueryResultSchema, ToolErrorCodeSchema } from './tools.js';

const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'data no formato AAAA-MM-DD');

/**
 * Plano de consulta independente de fonte. O núcleo monta a partir do catálogo;
 * o adaptador traduz para a linguagem da fonte (no Qlik, set analysis com valores escapados).
 */
export const QueryPlanSchema = z.object({
  source_id: IdSchema,
  app: z.string().min(1),
  measures: z.array(z.object({ id: z.string(), label: z.string(), expression: z.string() })).min(1),
  /** grain: a dimensão é um período derivado do campo de data (agrupa por dia, mês, trimestre ou ano). */
  dimensions: z.array(z.object({ id: z.string(), label: z.string(), field: z.string(), grain: GrainSchema.optional() })),
  /** Filtro por lista de valores, ou por intervalo de datas [from, to) em AAAA-MM-DD sobre um campo de data. */
  filters: z.array(
    z.union([
      /** exclude: tira os valores em vez de ficar só com eles. */
      z.object({ field: z.string(), values: z.array(z.string()).min(1), exclude: z.boolean().optional() }),
      z.object({ field: z.string(), range: z.object({ from: IsoDateSchema, to: IsoDateSchema }) }),
      // Condição sobre os valores do campo: número ou variável do app (o servidor escreve a busca do set analysis).
      z.object({ field: z.string(), condition: z.object({ op: z.enum(['<', '<=', '>', '>=', '=']), value: z.number().optional(), variable: z.string().optional() }) }),
      // Valores que começam com o texto (curinga só no fim).
      z.object({ field: z.string(), prefix: z.string().min(1) }),
    ]),
  ),
  /** Ordena por uma medida ou dimensão do plano (id). Sem sort: a maior medida primeiro, ou o tempo quando há grão. */
  sort: z.object({ by: z.string(), direction: z.enum(['asc', 'desc']) }).optional(),
  limit: z.number().int().min(1),
});
export type QueryPlan = z.infer<typeof QueryPlanSchema>;
export type PlanFilter = QueryPlan['filters'][number];
export type PlanDimension = QueryPlan['dimensions'][number];
export type PlanSort = NonNullable<QueryPlan['sort']>;

export const InterpretationRefSchema = z.object({
  prompt_version: z.string(),
  model_id: z.string(),
});
export type InterpretationRef = z.infer<typeof InterpretationRefSchema>;

/** O que fica guardado de cada resultado: base de explain_result, previous_result_id e replay. */
export const StoredResultSchema = z.object({
  tenant_id: TenantIdSchema,
  result: QueryResultSchema,
  principal: PrincipalSchema,
  plan: QueryPlanSchema,
  /** Usuário da fonte que executou (CLUSTER\maria). O replay roda como ele. */
  executed_as: z.string(),
  expressions_executed: z.array(z.string()),
  adapter: z.object({ type: z.string(), version: z.string() }),
  interpretation: InterpretationRefSchema.nullable(),
  lastrouter_version: z.string(),
});
export type StoredResult = z.infer<typeof StoredResultSchema>;

export const AuditStatusSchema = z.enum(['ok', 'clarification', 'no_answer', 'error']);
export type AuditStatus = z.infer<typeof AuditStatusSchema>;

/**
 * Uma linha por chamada de ferramenta. Sem valores do resultado.
 * Os campos seguem a tabela "O que cada linha de auditoria referencia".
 */
export const AuditRecordSchema = z.object({
  ts: z.string(),
  instance_id: z.string(),
  tenant_id: TenantIdSchema,
  correlation_id: z.string(),
  user: z.string(),
  user_subject: z.string(),
  channel: z.string(),
  client_app: z.string().nullable(),
  /** Nulo quando a chamada foi recusada antes de chegar a uma ferramenta (token, canal, limite). */
  tool: ToolNameSchema.nullable(),
  subject_id: IdSchema.nullable(),
  source_id: IdSchema.nullable(),
  app: z.string().nullable(),
  query_id: IdSchema.nullable(),
  query_version: z.number().int().nullable(),
  expressions: z.array(z.string()),
  publication_id: PublicationIdSchema.nullable(),
  prompt_version: z.string().nullable(),
  model_id: z.string().nullable(),
  lastrouter_version: z.string(),
  adapter_version: z.string().nullable(),
  source_last_reload_at: z.string().nullable(),
  status: AuditStatusSchema,
  error_code: ToolErrorCodeSchema.nullable(),
  rows: z.number().int().nullable(),
  duration_ms: z.number().int(),
  result_id: ResultIdSchema.nullable(),
  tokens: z.object({ input: z.number().int(), output: z.number().int() }).nullable(),
  /** Pergunta do usuário como o agente a enviou, quando a ferramenta recebe uma. Linhas antigas não têm. */
  question: z.string().max(1000).optional(),
  /** Corrente de hash (v1): sha256 do hash anterior com esta linha. Preenchido por quem grava. */
  hash: z.string().regex(/^[0-9a-f]{64}$/).optional(),
});
export type AuditRecord = z.infer<typeof AuditRecordSchema>;

export const FeedbackRecordSchema = z.object({
  id: z.string(),
  ts: z.string(),
  tenant_id: TenantIdSchema,
  result_id: ResultIdSchema,
  kind: FeedbackKindSchema,
  comment: z.string().nullable(),
  user: z.string(),
  channel: z.string(),
  subject_id: IdSchema,
  query_id: IdSchema,
  publication_id: PublicationIdSchema,
  /** Donos do assunto que recebem o pedido de revisão. */
  routed_to: z.array(z.string()),
});
export type FeedbackRecord = z.infer<typeof FeedbackRecordSchema>;
